import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { detailToSaveInput } from '../lib/detailToSaveInput';
import type { SaveWorkflowInput } from '../schemas/workflowSchemas';
import { workflowDetailDtoSchema } from '../schemas/workflowSchemas';
import { workflowKeys } from './workflowKeys';

interface CreateWorkflowResponse {
  id: string;
}

// Null every id so the full-save inserts fresh child rows into the NEW workflow
// (the source ids belong to the original and must not be reused).
function withFreshIds(body: SaveWorkflowInput): SaveWorkflowInput {
  return {
    ...body,
    steps: body.steps.map((s) => ({
      ...s,
      id: null,
      apiBindings: s.apiBindings.map((b) => ({
        ...b,
        id: null,
        inputMappings: b.inputMappings.map((m) => ({ ...m, id: null })),
      })),
      riskRules: s.riskRules.map((r) => ({ ...r, id: null })),
      conditions: s.conditions.map((c) => ({ ...c, id: null })),
      returnFields: s.returnFields.map((f) => ({ ...f, id: null })),
    })),
  };
}

// Duplicate a workflow — same as the "create workflow" flow, but pre-filled with a
// full copy of the source. Creates a fresh Draft shell, then full-saves the copied
// structure into it. Returns the new workflow id (Draft by default).
export function useDuplicateWorkflow() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async ({ workflowId, name }: { workflowId: string; name: string }) => {
      const raw = await apiClient.get(`api/v1/t/${tenantId}/workflows/${workflowId}`).json();
      const detail = workflowDetailDtoSchema.parse(raw);

      const created = await apiClient
        .post(`api/v1/t/${tenantId}/workflows`, {
          json: {
            name,
            templateType: detail.templateType || 'Blank',
            type: detail.type || 'KYC',
            structure: detail.structure || 'Simple',
          },
        })
        .json<CreateWorkflowResponse>();

      const body = withFreshIds(detailToSaveInput(detail, { name }));
      await apiClient.put(`api/v1/t/${tenantId}/workflows/${created.id}`, { json: body }).json();

      return created.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}
